import type { Point, FogReveal } from '@roll1/shared';

/**
 * Converts a visibility polygon into a FogReveal (polygon type) for persistence.
 * Stores as polygon to preserve wall-aware shape (circles extend through walls).
 * Points are rounded to integers to reduce storage.
 * Returns null if polygon has fewer than 3 points.
 */
export function visibilityToReveal(polygon: Point[]): FogReveal | null {
  if (polygon.length < 3) return null;
  return {
    type: 'polygon' as const,
    points: polygon.map(p => ({ x: Math.round(p.x), y: Math.round(p.y) })),
  };
}

/**
 * Computes new explored areas from current visibility that aren't covered
 * by previous explored areas. Returns FogReveal[] to append to explored.
 *
 * MVP approach: If the current visibility polygon differs from the last known
 * visibility (by centroid distance exceeding threshold), emit the entire
 * visibility polygon as a new explored area.
 *
 * @param currentVisibility - Current visibility polygon points
 * @param previousExplored - Already explored FogReveal areas
 * @param threshold - Minimum centroid distance change (world units) to trigger new explored area
 */
export function computeExploredDiff(
  currentVisibility: Point[],
  previousExplored: FogReveal[],
  threshold = 10,
): FogReveal[] {
  if (currentVisibility.length < 3) return [];

  // If no previous explored areas, the entire visibility is new
  if (previousExplored.length === 0) {
    const reveal = visibilityToReveal(currentVisibility);
    return reveal ? [reveal] : [];
  }

  // Compute centroid of current visibility
  const centroid = computeCentroid(currentVisibility);

  // Compare with centroid of most recent explored area
  const lastExplored = previousExplored[previousExplored.length - 1];
  let lastCentroid: Point | null = null;
  if (lastExplored.type === 'polygon') {
    lastCentroid = computeCentroid(lastExplored.points);
  } else if (lastExplored.type === 'circle') {
    lastCentroid = { x: lastExplored.x, y: lastExplored.y };
  }
  if (lastCentroid) {
    const dx = centroid.x - lastCentroid.x;
    const dy = centroid.y - lastCentroid.y;
    if (dx * dx + dy * dy < threshold * threshold) {
      return []; // Not enough movement to warrant new explored area
    }
  }

  const reveal = visibilityToReveal(currentVisibility);
  return reveal ? [reveal] : [];
}

/**
 * Simplifies explored areas by merging overlapping polygons.
 * MVP: pass-through (no merge) — returns input unchanged.
 */
export function simplifyExplored(explored: FogReveal[]): FogReveal[] {
  return explored;
}

/** Compute centroid of a polygon */
function computeCentroid(points: Point[]): Point {
  if (points.length === 0) return { x: 0, y: 0 };
  let sumX = 0;
  let sumY = 0;
  for (const p of points) {
    sumX += p.x;
    sumY += p.y;
  }
  return { x: sumX / points.length, y: sumY / points.length };
}
